import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CalendarClock,
  CheckCircle2,
  Clapperboard,
  CloudUpload,
  Download,
  Film,
  Image as ImageIcon,
  List,
  Loader2,
  Pencil,
  PlayCircle,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  UploadCloud,
  VideoIcon,
  X
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import {
  createExerciseVideo,
  deleteExerciseVideo,
  ExerciseVideo,
  ExerciseVideoPayload,
  getExerciseVideos,
  SubtitleCue,
  uploadExerciseVideoFileDirect,
  updateExerciseVideo
} from '../lib/api/exerciseVideos'
import { cn } from '../utils/cn'
import { buildHeaderIndex, downloadCsv, makeGetValue, normalizeText, parseCsvText } from '../utils/csv'

type PillProps = {
  children: ReactNode
  className?: string
}

const Pill = ({ children, className }: PillProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-1 text-[11px] font-medium text-foreground',
      className
    )}
  >
    {children}
  </span>
)

const initialForm: ExerciseVideoPayload = {
  key: '',
  title: '',
  description: '',
  videoUrl: '',
  videoObjectKey: '',
  thumbnailUrl: '',
  thumbnailObjectKey: '',
  subtitles: []
}

type UploadKind = 'video' | 'thumbnail'
type BulkUploadStatus = 'uploading' | 'saved' | 'failed'

type BulkUploadItem = {
  id: string
  fileName: string
  key: string
  status: BulkUploadStatus
  detail?: string
}

const toVideoKeyFromFileName = (fileName: string): string => {
  const withoutExtension = fileName.replace(/\.[^/.]+$/, '').trim().toLowerCase()
  const normalized = withoutExtension
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '')

  return normalized || 'untitled_video'
}

const toTitleFromKey = (key: string): string =>
  key
    .split(/[_-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')

const isMp4File = (file: File): boolean =>
  file.name.toLowerCase().endsWith('.mp4') || file.type.toLowerCase() === 'video/mp4'

const isThumbnailImageFile = (file: File): boolean => {
  const lowerName = file.name.toLowerCase()
  const lowerType = file.type.toLowerCase()
  return (
    lowerName.endsWith('.png') ||
    lowerName.endsWith('.jpg') ||
    lowerName.endsWith('.jpeg') ||
    lowerName.endsWith('.webp') ||
    lowerType === 'image/png' ||
    lowerType === 'image/jpeg' ||
    lowerType === 'image/webp'
  )
}

/* ── CSV import / export ──────────────────────────────────────────── */

const VIDEO_CSV_COLUMNS = [
  'Id',
  'Key',
  'Title',
  'Description',
  'VideoUrl',
  'VideoObjectKey',
  'ThumbnailUrl',
  'ThumbnailObjectKey',
  'Subtitles',
  'CreatedAt',
  'UpdatedAt'
] as const

type VideoCsvColumnKey = (typeof VIDEO_CSV_COLUMNS)[number]

const VIDEO_HEADER_ALIASES: Record<VideoCsvColumnKey, string[]> = {
  Id: ['id'],
  Key: ['key'],
  Title: ['title'],
  Description: ['description'],
  VideoUrl: ['videourl'],
  VideoObjectKey: ['videoobjectkey'],
  ThumbnailUrl: ['thumbnailurl'],
  ThumbnailObjectKey: ['thumbnailobjectkey'],
  Subtitles: ['subtitles'],
  CreatedAt: ['createdat'],
  UpdatedAt: ['updatedat']
}

const VIDEO_CSV_FALLBACK_INDEX: Record<VideoCsvColumnKey, number> = {
  Id: 0,
  Key: 1,
  Title: 2,
  Description: 3,
  VideoUrl: 4,
  VideoObjectKey: 5,
  ThumbnailUrl: 6,
  ThumbnailObjectKey: 7,
  Subtitles: 8,
  CreatedAt: 9,
  UpdatedAt: 10
}

const serializeSubtitles = (subtitles: SubtitleCue[]): string =>
  subtitles.length > 0 ? JSON.stringify(subtitles) : ''

const parseSubtitles = (value: string): SubtitleCue[] => {
  if (!value.trim()) return []
  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (cue: unknown) =>
        typeof cue === 'object' &&
        cue !== null &&
        'text' in cue &&
        'startMs' in cue &&
        'endMs' in cue
    ) as SubtitleCue[]
  } catch {
    return []
  }
}

export const ExerciseVideosPage = () => {
  const { addToast } = useToast()
  const [videos, setVideos] = useState<ExerciseVideo[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingKind, setUploadingKind] = useState<UploadKind | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [form, setForm] = useState<ExerciseVideoPayload>(initialForm)
  const [isBulkUploading, setIsBulkUploading] = useState(false)
  const [isDropTargetActive, setIsDropTargetActive] = useState(false)
  const [isThumbnailDropTargetActive, setIsThumbnailDropTargetActive] = useState(false)
  const [cardDropActiveId, setCardDropActiveId] = useState<string | null>(null)
  const [cardDropUploadId, setCardDropUploadId] = useState<string | null>(null)
  const [bulkUploads, setBulkUploads] = useState<BulkUploadItem[]>([])

  const [isCsvExporting, setIsCsvExporting] = useState(false)
  const [isCsvImporting, setIsCsvImporting] = useState(false)

  const videoInputRef = useRef<HTMLInputElement>(null)
  const thumbInputRef = useRef<HTMLInputElement>(null)
  const bulkVideoInputRef = useRef<HTMLInputElement>(null)
  const csvInputRef = useRef<HTMLInputElement>(null)

  const loadVideos = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getExerciseVideos()
      setVideos(data)
    } catch (error) {
      console.error('Failed to load exercise videos', error)
      addToast({
        title: 'Could not load videos',
        description: 'Please try again or check your connection.'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    void loadVideos()
  }, [loadVideos])

  const isEditing = Boolean(editingId)

  const resetForm = () => {
    setForm(initialForm)
    setEditingId(null)
    setFormError(null)
  }

  const handleFileSelect = (kind: UploadKind) => {
    if (kind === 'video') {
      videoInputRef.current?.click()
    } else {
      thumbInputRef.current?.click()
    }
  }

  const uploadFileToR2 = async (file: File, kind: UploadKind) => {
    setUploadingKind(kind)
    try {
      const upload = await uploadExerciseVideoFileDirect({
        file,
        kind,
        videoKey: form.key || undefined
      })

      setForm((prev) => ({
        ...prev,
        ...(kind === 'video'
          ? { videoUrl: upload.publicUrl, videoObjectKey: upload.objectKey }
          : { thumbnailUrl: upload.publicUrl, thumbnailObjectKey: upload.objectKey })
      }))

      addToast({
        title: kind === 'video' ? 'Video uploaded' : 'Thumbnail uploaded',
        description: 'Upload ready. Save the video record to use it.'
      })
    } catch (error) {
      console.error('Upload failed', error)
      addToast({
        title: 'Upload failed',
        description: 'Could not upload file. Check R2 configuration and try again.'
      })
    } finally {
      setUploadingKind(null)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, kind: UploadKind) => {
    const file = event.target.files?.[0]
    if (file) {
      void uploadFileToR2(file, kind)
    }
  }

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError(null)

    if (!form.key.trim() || !form.title.trim()) {
      setFormError('Key and title are required.')
      return
    }

    if (!form.videoUrl || !form.videoObjectKey) {
      setFormError('Upload a video before saving.')
      return
    }

    setSaving(true)
    try {
      const payload: ExerciseVideoPayload = {
        ...form,
        key: form.key.trim(),
        title: form.title.trim(),
        description: form.description?.trim() || null,
        thumbnailUrl: form.thumbnailUrl || null,
        thumbnailObjectKey: form.thumbnailObjectKey || null,
        subtitles: form.subtitles.map((cue) => ({
          ...cue,
          text: cue.text.trim(),
          language: cue.language.trim() || 'en'
        }))
      }

      let saved: ExerciseVideo
      if (editingId) {
        saved = await updateExerciseVideo(editingId, payload)
      } else {
        saved = await createExerciseVideo(payload)
      }

      setVideos((prev) => {
        const others = prev.filter((v) => v.id !== saved.id)
        return [saved, ...others].sort((a, b) => a.key.localeCompare(b.key))
      })

      addToast({
        title: 'Saved',
        description: editingId ? 'Video updated successfully.' : 'Video created successfully.'
      })
      resetForm()
    } catch (error) {
      console.error('Failed to save exercise video', error)
      setFormError('Could not save the video. Ensure the key is unique and try again.')
      addToast({
        title: 'Save failed',
        description: 'Could not save the video. Please check for duplicate keys.'
      })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (video: ExerciseVideo) => {
    setEditingId(video.id)
    setForm({
      key: video.key,
      title: video.title,
      description: video.description || '',
      videoUrl: video.videoUrl,
      videoObjectKey: video.videoObjectKey,
      thumbnailUrl: video.thumbnailUrl || '',
      thumbnailObjectKey: video.thumbnailObjectKey || '',
      subtitles: video.subtitles || []
    })
    setFormError(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id: string) => {
    const target = videos.find((v) => v.id === id)
    if (!target) return

    const confirmed = window.confirm(`Delete exercise video "${target.title}"?`)
    if (!confirmed) return

    try {
      await deleteExerciseVideo(id)
      setVideos((prev) => prev.filter((v) => v.id !== id))
      if (editingId === id) {
        resetForm()
      }
      addToast({ title: 'Deleted', description: 'Exercise video removed.' })
    } catch (error) {
      console.error('Failed to delete exercise video', error)
      addToast({
        title: 'Delete failed',
        description: 'Unable to delete this video right now.'
      })
    }
  }

  const handleVideoCardFileDrop = useCallback(async (video: ExerciseVideo, file: File) => {
    const kind: UploadKind | null = isMp4File(file)
      ? 'video'
      : isThumbnailImageFile(file)
        ? 'thumbnail'
        : null

    if (!kind) {
      addToast({
        title: 'Unsupported file type',
        description: 'Drop an MP4 to replace video, or PNG/JPG/WEBP to replace thumbnail.'
      })
      return
    }

    setCardDropUploadId(video.id)
    try {
      const upload = await uploadExerciseVideoFileDirect({
        file,
        kind,
        videoKey: video.key
      })

      const payload: ExerciseVideoPayload = kind === 'video'
        ? {
            key: video.key,
            title: video.title,
            description: video.description ?? null,
            videoUrl: upload.publicUrl,
            videoObjectKey: upload.objectKey,
            thumbnailUrl: video.thumbnailUrl ?? null,
            thumbnailObjectKey: video.thumbnailObjectKey ?? null,
            subtitles: video.subtitles
          }
        : {
            key: video.key,
            title: video.title,
            description: video.description ?? null,
            videoUrl: video.videoUrl,
            videoObjectKey: video.videoObjectKey,
            thumbnailUrl: upload.publicUrl,
            thumbnailObjectKey: upload.objectKey,
            subtitles: video.subtitles
          }

      const saved = await updateExerciseVideo(video.id, payload)
      setVideos((prev) => [saved, ...prev.filter((entry) => entry.id !== saved.id)].sort((a, b) => a.key.localeCompare(b.key)))

      if (editingId === video.id) {
        setForm((prev) => ({
          ...prev,
          ...(kind === 'video'
            ? { videoUrl: upload.publicUrl, videoObjectKey: upload.objectKey }
            : { thumbnailUrl: upload.publicUrl, thumbnailObjectKey: upload.objectKey })
        }))
      }

      addToast({
        title: kind === 'video' ? 'Video updated' : 'Thumbnail updated',
        description: `Updated ${video.title}.`
      })
    } catch (error) {
      console.error(`Card drop upload failed for ${video.key}`, error)
      addToast({
        title: 'Update failed',
        description: 'Could not update this video from drop upload.'
      })
    } finally {
      setCardDropUploadId(null)
    }
  }, [addToast, editingId])

  const handleVideoCardDrop = (event: React.DragEvent<HTMLDivElement>, video: ExerciseVideo) => {
    event.preventDefault()
    setCardDropActiveId(null)
    if (cardDropUploadId) {
      return
    }

    const file = Array.from(event.dataTransfer.files ?? [])[0]
    if (!file) {
      return
    }

    void handleVideoCardFileDrop(video, file)
  }

  const handleVideoCardDragOver = (event: React.DragEvent<HTMLDivElement>, videoId: string) => {
    event.preventDefault()
    if (!cardDropUploadId) {
      setCardDropActiveId(videoId)
    }
  }

  const handleVideoCardDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget as Node | null
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
      return
    }

    setCardDropActiveId(null)
  }

  const runBulkUpload = useCallback(async (files: File[]) => {
    if (isBulkUploading || files.length === 0) {
      return
    }

    const mp4Files = files.filter(isMp4File)
    if (mp4Files.length === 0) {
      addToast({
        title: 'No MP4 files found',
        description: 'Drop one or more .mp4 files to upload.'
      })
      return
    }

    const uploadQueue: BulkUploadItem[] = mp4Files.map((file, index) => ({
      id: `${file.name}-${file.size}-${index}`,
      fileName: file.name,
      key: toVideoKeyFromFileName(file.name),
      status: 'uploading'
    }))

    setBulkUploads(uploadQueue)
    setIsBulkUploading(true)

    let nextVideos = [...videos]
    let successCount = 0

    for (const [index, file] of mp4Files.entries()) {
      const item = uploadQueue[index]
      if (!item) continue

      try {
        const upload = await uploadExerciseVideoFileDirect({
          file,
          kind: 'video',
          videoKey: item.key
        })

        const existing = nextVideos.find((video) => video.key.toLowerCase() === item.key.toLowerCase())
        const payload: ExerciseVideoPayload = existing
          ? {
              key: existing.key,
              title: existing.title,
              description: existing.description ?? null,
              videoUrl: upload.publicUrl,
              videoObjectKey: upload.objectKey,
              thumbnailUrl: existing.thumbnailUrl ?? null,
              thumbnailObjectKey: existing.thumbnailObjectKey ?? null,
              subtitles: existing.subtitles
            }
          : {
              key: item.key,
              title: toTitleFromKey(item.key) || item.key,
              description: null,
              videoUrl: upload.publicUrl,
              videoObjectKey: upload.objectKey,
              thumbnailUrl: null,
              thumbnailObjectKey: null,
              subtitles: []
            }

        const saved = existing
          ? await updateExerciseVideo(existing.id, payload)
          : await createExerciseVideo(payload)

        nextVideos = [saved, ...nextVideos.filter((video) => video.id !== saved.id)]
          .sort((a, b) => a.key.localeCompare(b.key))
        setVideos(nextVideos)

        setBulkUploads((prev) =>
          prev.map((entry) =>
            entry.id === item.id
              ? {
                  ...entry,
                  status: 'saved',
                  detail: existing ? 'Overwrote existing video.' : 'Created new video.'
                }
              : entry
          )
        )
        successCount += 1
      } catch (error) {
        console.error(`Bulk upload failed for ${item.fileName}`, error)
        setBulkUploads((prev) =>
          prev.map((entry) =>
            entry.id === item.id
              ? {
                  ...entry,
                  status: 'failed',
                  detail: error instanceof Error ? error.message : 'Upload failed.'
                }
              : entry
          )
        )
      }
    }

    setIsBulkUploading(false)
    addToast({
      title: `Uploaded ${successCount}/${uploadQueue.length} video${uploadQueue.length === 1 ? '' : 's'}`,
      description:
        successCount === uploadQueue.length
          ? 'All files uploaded and mapped by filename key.'
          : 'Some files failed. Check the upload results list.'
    })
  }, [addToast, isBulkUploading, videos])

  const handleBulkDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDropTargetActive(false)
    if (isBulkUploading) {
      return
    }

    void runBulkUpload(Array.from(event.dataTransfer.files ?? []))
  }

  const handleBulkDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (!isBulkUploading) {
      setIsDropTargetActive(true)
    }
  }

  const handleBulkDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget as Node | null
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
      return
    }

    setIsDropTargetActive(false)
  }

  const handleBulkInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    if (files.length > 0) {
      void runBulkUpload(files)
    }
    event.target.value = ''
  }

  const handleThumbnailDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsThumbnailDropTargetActive(false)

    if (saving || isBulkUploading || uploadingKind !== null) {
      return
    }

    const files = Array.from(event.dataTransfer.files ?? [])
    const thumbnailFile = files.find(isThumbnailImageFile)
    if (!thumbnailFile) {
      addToast({
        title: 'No image found',
        description: 'Drop a PNG, JPG, or WEBP file for the thumbnail.'
      })
      return
    }

    void uploadFileToR2(thumbnailFile, 'thumbnail')
  }

  const handleThumbnailDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (!saving && !isBulkUploading && uploadingKind === null) {
      setIsThumbnailDropTargetActive(true)
    }
  }

  const handleThumbnailDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget as Node | null
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
      return
    }

    setIsThumbnailDropTargetActive(false)
  }

  /* ── CSV export / import handlers ────────────────────────────────── */

  const handleExportCsv = () => {
    setIsCsvExporting(true)
    try {
      const header = [...VIDEO_CSV_COLUMNS]
      const rows = videos.map((video) => [
        video.id,
        video.key,
        video.title,
        video.description ?? '',
        video.videoUrl,
        video.videoObjectKey,
        video.thumbnailUrl ?? '',
        video.thumbnailObjectKey ?? '',
        serializeSubtitles(video.subtitles),
        video.createdAt,
        video.updatedAt ?? ''
      ])

      const timestamp = new Date().toISOString().slice(0, 10)
      downloadCsv(header, rows, `hikestrong-exercise-videos-${timestamp}.csv`)
      addToast({ title: 'CSV exported', description: `Exported ${rows.length} exercise videos.` })
    } catch (error) {
      console.error('Failed to export exercise videos CSV', error)
      addToast({ title: 'Export failed', description: 'Could not export the exercise videos CSV file.' })
    } finally {
      setIsCsvExporting(false)
    }
  }

  const handleImportCsv = async (file: File) => {
    setIsCsvImporting(true)
    try {
      const text = await file.text()
      // Parse the whole document so quoted multi-line fields round-trip.
      const rows = parseCsvText(text)
      if (rows.length === 0) throw new Error('CSV file is empty.')

      const header = rows[0].map((value) => value.trim())
      const hasHeader =
        header.some((col) => normalizeText(col) === 'key') &&
        header.some((col) => normalizeText(col) === 'title')

      const dataRows = hasHeader ? rows.slice(1) : rows
      if (dataRows.length === 0) throw new Error('CSV appears to contain only headers.')

      const headerIndex = hasHeader ? buildHeaderIndex(header, VIDEO_HEADER_ALIASES) : null
      const getValue = makeGetValue(headerIndex, VIDEO_CSV_FALLBACK_INDEX)

      const existingByKey = new Map(videos.map((v) => [v.key.toLowerCase(), v]))

      let created = 0
      let updated = 0
      const errors: string[] = []

      for (const [index, row] of dataRows.entries()) {
        const lineNumber = hasHeader ? index + 2 : index + 1
        const key = getValue(row, 'Key').trim().toLowerCase()
        const title = getValue(row, 'Title').trim()
        const videoUrl = getValue(row, 'VideoUrl').trim()
        const videoObjectKey = getValue(row, 'VideoObjectKey').trim()

        if (!key || !title) {
          errors.push(`Row ${lineNumber}: missing required key or title.`)
          continue
        }

        if (!videoUrl || !videoObjectKey) {
          errors.push(`Row ${lineNumber}: missing videoUrl or videoObjectKey.`)
          continue
        }

        const payload: ExerciseVideoPayload = {
          key,
          title,
          description: getValue(row, 'Description') || null,
          videoUrl,
          videoObjectKey,
          thumbnailUrl: getValue(row, 'ThumbnailUrl') || null,
          thumbnailObjectKey: getValue(row, 'ThumbnailObjectKey') || null,
          subtitles: parseSubtitles(getValue(row, 'Subtitles'))
        }

        const existing = existingByKey.get(key)
        try {
          if (existing) {
            await updateExerciseVideo(existing.id, payload)
            updated += 1
          } else {
            await createExerciseVideo(payload)
            created += 1
          }
        } catch (error) {
          console.error(`Failed to import exercise video ${key}`, error)
          errors.push(`Row ${lineNumber} (${key}): save failed.`)
        }
      }

      if (created > 0 || updated > 0) {
        await loadVideos()
      }

      const details = []
      if (created > 0) details.push(`${created} created`)
      if (updated > 0) details.push(`${updated} updated`)
      const summary = details.length > 0 ? details.join(' and ') : 'No videos imported'

      if (errors.length > 0) {
        addToast({
          title: 'Import completed with errors',
          description: `${summary}. ${errors.length} failed.`
        })
      } else {
        addToast({ title: 'Import complete', description: `${summary}.` })
      }
    } catch (error) {
      console.error('Failed to import exercise videos CSV', error)
      addToast({
        title: 'Import failed',
        description: error instanceof Error ? error.message : 'Could not parse or import the CSV file.'
      })
    } finally {
      setIsCsvImporting(false)
    }
  }

  const handleAddSubtitle = (cue: SubtitleCue) => {
    if (!cue.text.trim()) return
    setForm((prev) => ({
      ...prev,
      subtitles: [...prev.subtitles, cue].sort((a, b) => a.startMs - b.startMs)
    }))
  }

  const handleRemoveSubtitle = (index: number) => {
    setForm((prev) => ({
      ...prev,
      subtitles: prev.subtitles.filter((_, i) => i !== index)
    }))
  }

  const subtitleCount = form.subtitles.length

  const formattedVideos = useMemo(
    () => [...videos].sort((a, b) => a.key.localeCompare(b.key)),
    [videos]
  )

  const renderVideoForm = (showCancel: boolean) => (
    <form className="space-y-4" onSubmit={handleSave}>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground" htmlFor="video-key">
            Key
          </label>
          <Input
            id="video-key"
            value={form.key}
            onChange={(e) => setForm((prev) => ({ ...prev, key: e.target.value }))}
            placeholder="trail_walk_easy"
            disabled={saving}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground" htmlFor="video-title">
            Title
          </label>
          <Input
            id="video-title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Trail Walk - Easy"
            disabled={saving}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground" htmlFor="video-description">
          Description (optional)
        </label>
        <Textarea
          id="video-description"
          value={form.description ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          rows={2}
          placeholder="Short context for the exercise video."
          disabled={saving}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <span className="text-sm font-medium text-foreground">Video</span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              className="gap-2"
              onClick={() => handleFileSelect('video')}
              disabled={saving || isBulkUploading || uploadingKind === 'video'}
            >
              {uploadingKind === 'video' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CloudUpload className="h-4 w-4" />
              )}
              {form.videoUrl ? 'Replace video' : 'Upload video'}
            </Button>
            {form.videoUrl ? (
              <Pill className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                uploaded
              </Pill>
            ) : (
              <Pill className="text-amber-700 border-amber-200 bg-transparent">
                MP4 required
              </Pill>
            )}
          </div>
          {form.videoUrl ? (
            <p className="text-xs text-muted-foreground break-all">{form.videoUrl}</p>
          ) : (
            <p className="text-xs text-muted-foreground">Upload an MP4 to storage.</p>
          )}
        </div>

        <div className="space-y-2">
          <span className="text-sm font-medium text-foreground">Thumbnail</span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => handleFileSelect('thumbnail')}
              disabled={saving || isBulkUploading || uploadingKind === 'thumbnail'}
            >
              {uploadingKind === 'thumbnail' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImageIcon className="h-4 w-4" />
              )}
              {form.thumbnailUrl ? 'Replace thumbnail' : 'Upload thumbnail'}
            </Button>
            {form.thumbnailUrl ? (
              <Pill className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                set
              </Pill>
            ) : (
              <Pill className="bg-transparent text-muted-foreground">Optional</Pill>
            )}
          </div>
          {form.thumbnailUrl ? (
            <p className="text-xs text-muted-foreground break-all">{form.thumbnailUrl}</p>
          ) : (
            <p className="text-xs text-muted-foreground">PNG/JPG recommended 16:9.</p>
          )}
          {isEditing ? (
            <div
              role="button"
              tabIndex={0}
              onClick={() => {
                if (!saving && !isBulkUploading && uploadingKind === null) {
                  thumbInputRef.current?.click()
                }
              }}
              onKeyDown={(event) => {
                if ((event.key === 'Enter' || event.key === ' ') && !saving && !isBulkUploading && uploadingKind === null) {
                  event.preventDefault()
                  thumbInputRef.current?.click()
                }
              }}
              onDrop={handleThumbnailDrop}
              onDragOver={handleThumbnailDragOver}
              onDragLeave={handleThumbnailDragLeave}
              className={cn(
                'mt-2 rounded-md border border-dashed px-3 py-2 text-xs transition-colors',
                isThumbnailDropTargetActive
                  ? 'border-primary bg-primary/5 text-foreground'
                  : 'border-border text-muted-foreground',
                (saving || isBulkUploading || uploadingKind !== null)
                  ? 'cursor-not-allowed opacity-70'
                  : 'cursor-pointer hover:border-primary/60 hover:bg-primary/5'
              )}
            >
              Drop thumbnail image here or click to choose
            </div>
          ) : null}
        </div>
      </div>

      <SubtitleEditor
        subtitles={form.subtitles}
        onAdd={handleAddSubtitle}
        onRemove={handleRemoveSubtitle}
        disabled={saving}
      />

      {formError ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {formError}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={saving || isBulkUploading || uploadingKind !== null} className="gap-2">
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          {isEditing ? 'Save changes' : 'Add video'}
        </Button>
        {showCancel ? (
          <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
            Cancel edit
          </Button>
        ) : null}
      </div>
    </form>
  )

  return (
    <div className="space-y-6">
      <div className="grid gap-4 xl:grid-cols-3">
        <div className="xl:col-span-1 space-y-4">
          <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 shadow-xl">
            <CardHeader className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-emerald-500/20 p-3 ring-1 ring-emerald-500/30">
                  <Clapperboard className="h-6 w-6 text-emerald-200" />
                </div>
                <div>
                  <CardTitle className="text-lg">Exercise Demo Videos</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Upload, subtitle, and link videos to exercises.
                  </CardDescription>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wide text-emerald-200/80 font-semibold">
                <VideoIcon className="h-4 w-4" />
                Cached on device
                <Pill className="bg-slate-100 text-slate-800 border border-slate-200">R2</Pill>
                <Pill className="bg-emerald-500/20 text-emerald-100 border border-emerald-500/40">
                  Subtitles {subtitleCount}
                </Pill>
              </div>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>Use a unique key to match exercises (e.g. trail_walk_easy). Upload the MP4 and optional thumbnail to R2 before saving.</p>
              <p>You can now drop files directly onto video cards too: MP4 updates the video, image files update the thumbnail.</p>
              <p>Subtitles use millisecond timings and display in the mobile player.</p>
            </CardContent>
          </Card>

          <Card className="shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <UploadCloud className="h-5 w-5 text-primary" />
                Drop MP4s To Auto-Upload
              </CardTitle>
              <CardDescription>
                Key = filename without extension. Each upload creates a new file and creates or updates the video record.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div
                role="button"
                tabIndex={0}
                onClick={() => !isBulkUploading && bulkVideoInputRef.current?.click()}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && !isBulkUploading) {
                    event.preventDefault()
                    bulkVideoInputRef.current?.click()
                  }
                }}
                onDrop={handleBulkDrop}
                onDragOver={handleBulkDragOver}
                onDragLeave={handleBulkDragLeave}
                className={cn(
                  'rounded-lg border-2 border-dashed p-4 text-center transition-colors',
                  isDropTargetActive
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-muted/20',
                  isBulkUploading ? 'cursor-wait opacity-80' : 'cursor-pointer hover:border-primary/60 hover:bg-primary/5'
                )}
              >
                <UploadCloud className="mx-auto mb-2 h-6 w-6 text-primary" />
                <p className="text-sm font-medium text-foreground">
                  {isBulkUploading ? 'Uploading videos...' : 'Drop MP4 files here or click to select'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Example: <span className="font-mono">step_up_easy.mp4</span> {'->'} key <span className="font-mono">step_up_easy</span>
                </p>
              </div>

              {bulkUploads.length > 0 ? (
                <div className="max-h-52 space-y-2 overflow-auto rounded-md border border-border p-2">
                  {bulkUploads.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start justify-between gap-3 rounded border border-border/70 bg-background px-2 py-1.5"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-foreground">{item.fileName}</p>
                        <p className="text-[11px] text-muted-foreground font-mono">{item.key}</p>
                        {item.detail ? (
                          <p className={cn('text-[11px]', item.status === 'failed' ? 'text-red-600' : 'text-muted-foreground')}>
                            {item.detail}
                          </p>
                        ) : null}
                      </div>
                      {item.status === 'uploading' ? (
                        <Loader2 className="h-4 w-4 animate-spin text-primary mt-0.5" />
                      ) : item.status === 'saved' ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                      ) : (
                        <X className="h-4 w-4 text-red-600 mt-0.5" />
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {!isEditing ? (
            <Card className="shadow-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Film className="h-5 w-5 text-primary" />
                      Add a video
                    </CardTitle>
                    <CardDescription>Keys should be unique and stable.</CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => void loadVideos()}
                    title="Reload videos"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>{renderVideoForm(false)}</CardContent>
            </Card>
          ) : null}

          <Dialog
            open={isEditing}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                resetForm()
              }
            }}
          >
            <DialogContent className="w-[min(96vw,980px)]">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Film className="h-5 w-5 text-primary" />
                Edit video
              </DialogTitle>
              <DialogDescription>Update video details, media, or subtitles.</DialogDescription>
              {renderVideoForm(true)}
            </DialogContent>
          </Dialog>
        </div>

        <div className="xl:col-span-2 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Video Library</h2>
              <p className="text-sm text-muted-foreground">
                {formattedVideos.length} video{formattedVideos.length === 1 ? '' : 's'} in R2.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) {
                    void handleImportCsv(file)
                  }
                  event.target.value = ''
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => csvInputRef.current?.click()}
                disabled={isCsvImporting || loading || isBulkUploading}
                className="gap-2"
              >
                {isCsvImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Import CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCsv}
                disabled={isCsvExporting || loading || videos.length === 0}
                className="gap-2"
              >
                {isCsvExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export CSV
              </Button>
            </div>
          </div>

          {loading ? (
            <Card className="flex items-center gap-3 px-6 py-5">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading videos…</p>
            </Card>
          ) : formattedVideos.length === 0 ? (
            <EmptyState
              icon={Clapperboard}
              title="No exercise videos yet"
              description="Upload a demo video to get started."
            >
              <Button onClick={() => document.getElementById('video-key')?.focus()} className="gap-2">
                <UploadCloud className="h-4 w-4" />
                Add video
              </Button>
            </EmptyState>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {formattedVideos.map((video) => {
                const isCardDropActive = cardDropActiveId === video.id
                const isCardUploading = cardDropUploadId === video.id

                return (
                <Card
                  key={video.id}
                  className={cn(
                    'flex flex-col justify-between shadow-sm transition-colors',
                    isCardDropActive ? 'border-primary bg-primary/5' : '',
                    isCardUploading ? 'opacity-80' : ''
                  )}
                  onDrop={(event) => handleVideoCardDrop(event, video)}
                  onDragOver={(event) => handleVideoCardDragOver(event, video.id)}
                  onDragLeave={handleVideoCardDragLeave}
                >
                  <CardHeader className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-lg flex items-start gap-2">
                        <PlayCircle className="mt-1 h-4 w-4 text-primary" />
                        <span>{video.title}</span>
                      </CardTitle>
                      <Pill className="bg-muted text-foreground border border-border/60 text-xs">
                        {video.key}
                      </Pill>
                    </div>
                    {video.description ? (
                      <CardDescription className="text-sm leading-relaxed text-foreground">
                        {video.description}
                      </CardDescription>
                    ) : null}
                    <div className="flex flex-wrap gap-2 text-xs">
                      <Pill className="flex items-center gap-1">
                        <CalendarClock className="h-3 w-3" />
                        {new Date(video.createdAt).toLocaleDateString()}
                      </Pill>
                      <Pill className={cn('flex items-center gap-1', video.thumbnailUrl ? '' : 'text-amber-700 border-amber-200 bg-transparent')}>
                        <ImageIcon className="h-3 w-3" />
                        {video.thumbnailUrl ? 'Thumbnail' : 'No thumbnail'}
                      </Pill>
                      <Pill className={cn('flex items-center gap-1', video.subtitles.length ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : 'text-muted-foreground')}>
                        <List className="h-3 w-3" />
                        {video.subtitles.length} subtitles
                      </Pill>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Drop MP4 to replace video, or image to replace thumbnail.
                    </p>
                  </CardHeader>
                  <CardContent className="flex items-center justify-between">
                    <div className="text-[11px] text-muted-foreground break-all max-w-[70%]">
                      <p className="font-mono">Video: {video.videoObjectKey}</p>
                      {video.thumbnailObjectKey ? <p className="font-mono">Thumb: {video.thumbnailObjectKey}</p> : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(video)}
                        className="gap-1"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => void handleDelete(video.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )})}
            </div>
          )}
        </div>
      </div>

      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/*"
        className="hidden"
        onChange={(e) => handleFileChange(e, 'video')}
      />
      <input
        ref={bulkVideoInputRef}
        type="file"
        accept="video/mp4"
        multiple
        className="hidden"
        onChange={handleBulkInputChange}
      />
      <input
        ref={thumbInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        className="hidden"
        onChange={(e) => handleFileChange(e, 'thumbnail')}
      />
    </div>
  )
}

type SubtitleEditorProps = {
  subtitles: SubtitleCue[]
  onAdd: (cue: SubtitleCue) => void
  onRemove: (index: number) => void
  disabled?: boolean
}

const SubtitleEditor = ({ subtitles, onAdd, onRemove, disabled }: SubtitleEditorProps) => {
  const [draft, setDraft] = useState<SubtitleCue>({
    text: '',
    startMs: 0,
    endMs: 0,
    language: 'en'
  })

  const handleAdd = () => {
    if (!draft.text.trim()) return
    if (draft.endMs > 0 && draft.endMs < draft.startMs) return
    onAdd({ ...draft, text: draft.text.trim(), language: draft.language.trim() || 'en' })
    setDraft((prev) => ({ ...prev, text: '' }))
  }

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pill className="flex items-center gap-1">
            <Clapperboard className="h-3 w-3" />
            Subtitles
          </Pill>
          <span className="text-xs text-muted-foreground">{subtitles.length} cue{subtitles.length === 1 ? '' : 's'}</span>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <Input
          type="number"
          min={0}
          value={draft.startMs}
          onChange={(e) => setDraft((prev) => ({ ...prev, startMs: Number(e.target.value) }))}
          placeholder="Start (ms)"
          disabled={disabled}
        />
        <Input
          type="number"
          min={0}
          value={draft.endMs}
          onChange={(e) => setDraft((prev) => ({ ...prev, endMs: Number(e.target.value) }))}
          placeholder="End (ms)"
          disabled={disabled}
        />
        <Input
          value={draft.language}
          onChange={(e) => setDraft((prev) => ({ ...prev, language: e.target.value }))}
          placeholder="Language"
          disabled={disabled}
        />
        <Button type="button" variant="secondary" className="gap-2" onClick={handleAdd} disabled={disabled}>
          <Plus className="h-4 w-4" />
          Add cue
        </Button>
      </div>
      <Textarea
        value={draft.text}
        onChange={(e) => setDraft((prev) => ({ ...prev, text: e.target.value }))}
        placeholder="Subtitle text"
        rows={2}
        disabled={disabled}
      />

      {subtitles.length > 0 ? (
        <div className="space-y-2 rounded-md bg-muted/50 p-2 max-h-52 overflow-y-auto">
          {subtitles.map((cue, index) => (
            <div key={`${cue.startMs}-${index}`} className="flex items-start justify-between gap-2 rounded-md bg-background px-3 py-2 border border-border/60">
              <div className="text-xs">
                <p className="font-mono text-muted-foreground">
                  {cue.startMs}ms → {cue.endMs}ms ({cue.language})
                </p>
                <p className="text-sm text-foreground">{cue.text}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onRemove(index)}
                className="text-red-600 hover:bg-red-50"
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Add timed lines to show in the mobile player.</p>
      )}
    </div>
  )
}
